import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

interface PriceHistoryEntry {
  id: string;
  restaurant_id: string;
  product_id: string;
  old_price: number;
  new_price: number;
  changed_by: string;
  created_at: string;
}

export function usePriceHistory() {
  const { activeRestaurant } = useRestaurantContext();
  const [history, setHistory] = useState<PriceHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchHistory = useCallback(async (productId?: string) => {
    if (!activeRestaurant) return;
    
    setIsLoading(true);
    try {
      let query = supabase
        .from("price_history" as any)
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .order("created_at", { ascending: false })
        .limit(100);

      if (productId) {
        query = query.eq("product_id", productId);
      }

      const { data, error } = await query as any;

      if (error) throw error;
      setHistory((data as PriceHistoryEntry[]) || []);
    } catch (error) {
      console.error("Error fetching price history:", error);
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant]);

  const recordPriceChange = useCallback(async (
    productId: string, 
    oldPrice: number, 
    newPrice: number
  ) => {
    if (!activeRestaurant) return false;
    if (oldPrice === newPrice) return true; // No change needed

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { error } = await supabase
        .from("price_history" as any)
        .insert({
          restaurant_id: activeRestaurant.id,
          product_id: productId,
          old_price: oldPrice,
          new_price: newPrice,
          changed_by: user.id,
        });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error("Error recording price change:", error);
      return false;
    }
  }, [activeRestaurant]);

  const getProductHistory = useCallback((productId: string): PriceHistoryEntry[] => {
    return history.filter(h => h.product_id === productId);
  }, [history]);

  return {
    history,
    isLoading,
    fetchHistory,
    recordPriceChange,
    getProductHistory,
  };
}
